import * as S from "./styles.ts";
import { Editable, useSlate, useSlateStatic } from "slate-react";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Editor, Path, Transforms, Node, Element, Range } from "slate";
import {
  renderElement,
  renderLeaf,
} from "@admin/config/overrides/fieldTypes/MarkdownEditor/helpers";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";

// const getFirstChildPath: any = (path: any, existingPath?: number[]) => {
//   let pathArr = existingPath || [0];
//   if (path[0].children) {
//     pathArr.push(0);
//     return getFirstChildPath(path[0].children, pathArr);
//   }
//   return pathArr;
// };

function useResetMarksOnMount(
  containerRef: React.RefObject<HTMLElement | null>
) {
  const editor = useSlateStatic();

  useEffect(() => {
    // 🔹 Clear marks once on mount
    const marks = Editor.marks(editor);
    if (marks) {
      Object.keys(marks).forEach((key) => {
        Editor.removeMark(editor, key);
      });
    }

    // 🔹 Scope to this editor's container, not a global query
    const container = containerRef.current;
    if (!container) return;
    const root = container.querySelector("[data-slate-editor='true']");
    if (!root) return;

    const handleBlur = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;

      // Don't clear marks if focus stays within the editor container or a popup
      const editorContainer = container.closest("[data-markdown-editor]");
      if (
        relatedTarget &&
        ((editorContainer && editorContainer.contains(relatedTarget)) ||
          relatedTarget.closest("[data-popper-component]") ||
          relatedTarget.closest("[data-is-popup]"))
      ) {
        return;
      }

      // Delay clearing marks so keyboard-triggered blurs (null relatedTarget)
      // that immediately re-focus don't wipe state mid-operation.
      setTimeout(() => {
        if (root.contains(document.activeElement)) return;

        try {
          // Validate selection paths still exist before clearing marks
          const { selection } = editor;
          if (selection) {
            Node.get(editor, selection.anchor.path);
            Node.get(editor, selection.focus.path);
          }

          const currentMarks = Editor.marks(editor);
          if (currentMarks) {
            Object.keys(currentMarks).forEach((key) =>
              Editor.removeMark(editor, key)
            );
          }
        } catch {
          // Selection references stale paths after a delete; skip clearing
        }
      }, 0);
    };

    root.addEventListener("blur", handleBlur, true);

    return () => {
      root.removeEventListener("blur", handleBlur, true);
    };
  }, [editor]);
}

const EditorContent = ({ disabled }) => {
  const editor = useSlate();
  const [mounted, setMounted] = useState(false);
  const currency = useCurrency();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useResetMarksOnMount(wrapperRef);
  useEffect(() => {
    if (!mounted) {
      setMounted(true);
      setTimeout(() => {
        try {
          const start = Editor.start(editor, [0]);
          Transforms.select(editor, { anchor: start, focus: start });
        } catch {
          // Content not ready yet; ignore
        }
      }, 50);
    }
  }, [mounted]);

  const handleKeyDown = (editor: Editor) => (event: KeyboardEvent) => {
    // =========================
    // Arrow keys: escape link marks at node boundaries
    // =========================
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      const { selection } = editor;
      if (!selection || !Range.isCollapsed(selection)) return;

      const marks = Editor.marks(editor);
      if (!marks || !(marks as any).link) return;

      const { offset } = selection.focus;
      const [node] = Editor.node(editor, selection.focus.path);

      if (!node || !("text" in node)) return;

      const atEnd =
        event.key === "ArrowRight" && offset === (node as any).text.length;
      const atStart = event.key === "ArrowLeft" && offset === 0;

      if (atEnd || atStart) {
        // Let Slate move the cursor first, then clear link marks
        setTimeout(() => {
          Editor.removeMark(editor, "link");
          Editor.removeMark(editor, "url");
          Editor.removeMark(editor, "title");
        }, 0);
      }
    }

    // =========================
    // ENTER → new list-item or exit list
    // =========================
    if (event.key === "Enter") {
      const { selection } = editor;
      if (!selection) return;

      const liEntry = Editor.above(editor, {
        match: (n) => Element.isElement(n) && (n as any).type === "list-item",
      });
      if (!liEntry) return;

      event.preventDefault();

      const [, liPath] = liEntry;
      const parentListEntry = Editor.above(editor, {
        at: liPath,
        match: (n) =>
          Element.isElement(n) && ["ol", "ul"].includes((n as any).type),
      });
      if (!parentListEntry) return;
      const [, listPath] = parentListEntry;

      // const listType = (
      //   Node.get(editor, listPath) as Element & { type: string }
      // ).type as "ol" | "ul";

      const newListItem = {
        type: "list-item",
        children: [{ type: "paragraph", children: [{ text: "" }] }],
      };

      const currentLi = Node.get(editor, liPath) as Element;
      const isEmpty =
        currentLi.children.length === 1 &&
        Element.isElement(currentLi.children[0]) &&
        (currentLi as any).children[0].type === "paragraph" &&
        Node.string(currentLi.children[0]) === "";

      if (isEmpty) {
        // Exit ALL lists (like Word): turn this empty item into a top-level paragraph
        Editor.withoutNormalizing(editor, () => {
          // Find the nearest list for this li
          const nearestListEntry = Editor.above(editor, {
            at: liPath,
            match: (n) =>
              Element.isElement(n) && ["ol", "ul"].includes((n as any).type),
          });
          if (!nearestListEntry) return;
          const [, nearestListPath] = nearestListEntry;

          // Walk up to OUTERMOST list
          let topListPath = nearestListPath;
          while (true) {
            const higher = Editor.above(editor, {
              at: topListPath,
              match: (n) =>
                Element.isElement(n) && ["ol", "ul"].includes((n as any).type),
            });
            if (!higher) break;
            const [, higherPath] = higher;
            topListPath = higherPath;
          }

          // Destination: AFTER the outermost list
          const destParent = Path.parent(topListPath);
          const listIndex = topListPath[topListPath.length - 1];
          const paraDest = destParent.concat(listIndex + 1);

          // 1) Insert the paragraph at top-level after the whole list cluster
          (Transforms as any).insertNodes(
            editor,
            { type: "paragraph", children: [{ text: "" }] },
            { at: paraDest }
          );

          // 2) Remove the original empty <li>
          Transforms.removeNodes(editor, { at: liPath });

          // 3) Clean up any empty lists on the way up (outermost first is safest)
          // Try to remove emptied lists starting from nearest up to top
          const candidates: Path[] = [];
          {
            // collect list ancestor paths from nearest → outermost
            let cursor: Path | null = nearestListPath;
            while (cursor) {
              candidates.push(cursor);
              const up = Editor.above(editor, {
                at: cursor,
                match: (n) =>
                  Element.isElement(n) &&
                  ["ol", "ul"].includes((n as any).type),
              });
              cursor = up ? (up[1] as Path) : null;
            }
          }
          // Remove empty ones (re-read each time because tree changes)
          for (const p of candidates) {
            try {
              const maybe = Node.get(editor, p);
              if (Element.isElement(maybe) && maybe.children.length === 0) {
                Transforms.removeNodes(editor, { at: p });
              }
            } catch {
              /* path may be gone already; ignore */
            }
          }

          // 4) Put the caret into the new paragraph
          const anchor = Editor.start(editor, paraDest);
          Transforms.select(editor, { anchor, focus: anchor });
        });

        return;
      }

      // Otherwise insert new sibling li
      const dest = Path.next(liPath);
      Transforms.insertNodes(editor, newListItem, { at: dest });
      const anchor = Editor.start(editor, dest.concat(0));
      Transforms.select(editor, { anchor, focus: anchor });
      return;
    }

    // =========================
    // TAB / SHIFT+TAB
    // =========================
    if (event.key === "Tab") {
      event.preventDefault();
      const { selection } = editor;
      if (!selection) return;

      const liEntry = Editor.above(editor, {
        match: (n) => Element.isElement(n) && (n as any).type === "list-item",
      });
      if (!liEntry) return;

      const [, liPath] = liEntry;

      // SHIFT+TAB → OUTDENT
      if (event.shiftKey) {
        const parentListEntry = Editor.above(editor, {
          at: liPath,
          match: (n) =>
            Element.isElement(n) && ["ol", "ul"].includes((n as any).type),
        });
        if (!parentListEntry) return;

        const [, parentListPath] = parentListEntry;
        const parentLiEntry = Editor.above(editor, {
          at: parentListPath,
          match: (n) => Element.isElement(n) && (n as any).type === "list-item",
        });

        // prevent outdenting past outermost list
        const maybeParentListParent = Editor.above(editor, {
          at: parentListPath,
          match: (n) =>
            Element.isElement(n) && ["ol", "ul"].includes((n as any).type),
        });
        if (!maybeParentListParent && !parentLiEntry) {
          return;
        }

        Editor.withoutNormalizing(editor, () => {
          let destination: Path;
          if (parentLiEntry) {
            const [, parentLiPath] = parentLiEntry;
            destination = Path.next(parentLiPath);
          } else {
            const destinationParentPath = Path.parent(parentListPath);
            const destinationIndex =
              parentListPath[parentListPath.length - 1] + 1;
            destination = destinationParentPath.concat(destinationIndex);
          }

          Transforms.moveNodes(editor, { at: liPath, to: destination });

          // remove empty list after outdent
          try {
            const maybeList = Node.get(editor, parentListPath);
            if (
              Element.isElement(maybeList) &&
              maybeList.children.length === 0
            ) {
              Transforms.removeNodes(editor, { at: parentListPath });
            }
          } catch {}

          Transforms.setNodes(editor, {}, { at: destination });
        });

        return;
      }

      // =========================
      // TAB → INDENT (supports cross-type nesting: OL→UL, UL→OL)
      // =========================
      if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        const { selection } = editor;
        if (!selection) return;

        const liEntry = Editor.above(editor, {
          match: (n) => Element.isElement(n) && (n as any).type === "list-item",
        });
        if (!liEntry) return;
        const [liNode, liPath] = liEntry;

        // find previous list item
        const prevEntry = Editor.previous(editor, {
          at: liPath,
          match: (n) => Element.isElement(n) && (n as any).type === "list-item",
        });
        if (!prevEntry) return; // cannot indent first item
        const [, prevLiPath] = prevEntry;

        Editor.withoutNormalizing(editor, () => {
          Transforms.deselect(editor);

          // find the parent list type (ol or ul)
          const parentListEntry = Editor.above(editor, {
            at: liPath,
            match: (n) =>
              Element.isElement(n) && ["ol", "ul"].includes((n as any).type),
          });
          if (!parentListEntry) return;
          const [parentListNode] = parentListEntry as [Element, Path];
          const parentType = (parentListNode as any).type as "ol" | "ul";

          // 🔄 Alt+Tab = opposite type
          const nestedType: "ol" | "ul" = event.altKey
            ? parentType === "ol"
              ? "ul"
              : "ol"
            : parentType;

          const prevLi = Node.get(editor, prevLiPath) as Element;

          // check if prevLi already has a nested list of this type
          let nestedListPath: Path;
          const existingNestedIndex = prevLi.children.findIndex(
            (c: any) =>
              Element.isElement(c) && ["ol", "ul"].includes((c as any).type)
          );

          if (existingNestedIndex >= 0) {
            nestedListPath = prevLiPath.concat(existingNestedIndex);
            const existingList = Node.get(editor, nestedListPath) as Element;
            if ((existingList as any).type !== nestedType) {
              // convert type if Alt+Tab changes it
              (Transforms as any).setNodes(
                editor,
                { type: nestedType },
                { at: nestedListPath }
              );
            }
          } else {
            // create a new nested list under prev <li>
            nestedListPath = prevLiPath.concat(prevLi.children.length);
            (Transforms as any).insertNodes(
              editor,
              { type: nestedType, children: [] },
              { at: nestedListPath }
            );
          }

          // deep clone current li node
          const liClone = JSON.parse(JSON.stringify(liNode));

          // remove original li
          Transforms.removeNodes(editor, { at: liPath });

          // move clone into nested list
          const nestedList = Node.get(editor, nestedListPath) as Element;
          const dest = nestedListPath.concat(nestedList.children.length);
          Transforms.insertNodes(editor, liClone, { at: dest });

          // move caret to start of moved item
          const anchor = Editor.start(editor, dest);
          Transforms.select(editor, { anchor, focus: anchor });
        });

        return;
      }
    }

    // ===========================================
    // DELETE when all content is selected
    // ===========================================
    if (event.key === "Backspace" || event.key === "Delete") {
      const { selection } = editor;
      if (!selection) return;

      const allText = Editor.string(editor, []);
      const selectedText = Editor.string(editor, selection);

      // detect if all content is selected
      const allSelected =
        Range.isExpanded(selection) && selectedText === allText;

      if (allSelected) {
        event.preventDefault();

        Editor.withoutNormalizing(editor, () => {
          while (editor.children.length > 0) {
            Transforms.removeNodes(editor, { at: [0] });
          }
          (Transforms as any).insertNodes(editor, {
            type: "paragraph",
            children: [{ text: "" }],
          });
          Editor.removeMark(editor, "bold");
          Editor.removeMark(editor, "italic");
          Editor.removeMark(editor, "underline");
          Editor.removeMark(editor, "link");
          Editor.removeMark(editor, "variant");
          Editor.removeMark(editor, "us");
          Editor.removeMark(editor, "uk");
          Transforms.select(editor, Editor.start(editor, [0]));
        });
        return;
      }
    }
  };

  return (
    <S.EditorContentWrapper
      $disabled={disabled}
      ref={wrapperRef}
    >
      <S.EditorContent>
        <Editable
          renderElement={renderElement}
          renderLeaf={(props) => renderLeaf(props, currency)}
          autoFocus
          disabled={disabled}
          onKeyDown={handleKeyDown(editor)}
        />
      </S.EditorContent>
    </S.EditorContentWrapper>
  );
};

export default EditorContent;
